/**
 * PATH: packages/ui/src/kanban/components/KanbanCardFaceSlots.tsx
 * FILE_ID: UI-KANBAN-CARD-FACE-SLOTS-001
 * PURPOSE: Build KanbanCardFace slots from the resolved face recipe.
 * ROLE IN ARCHITECTURE: Presentational slot map. KanbanCard stays the frame.
 * MAIN EXPORTS: buildKanbanCardFaceSlots, CARD_FACE_SLOT_FIELD_IDS
 * NON-RESPONSIBILITIES: Does not persist or own hover state.
 * NOTES FOR FUTURE AI: Hide omits chrome. Complete off omits the handler.
 */
import type { Dispatch, ReactNode, SetStateAction } from 'react'

import type { ResolvedCardFacePrefs } from './board-view/cardFacePrefs'
import type { KanbanCardProps, KanbanCardQuickActionHandlers } from './KanbanCard.types'
import { KanbanCardActionLine, KanbanCardStateStrip } from './KanbanCardActionLine'
import { KanbanCardEmailMeta } from './KanbanCardEmailMeta'
import { KanbanCardFooter } from './KanbanCardFooter'
import {
  KanbanCardInteractionControls,
  type KanbanCardInteractionControlsProps,
} from './KanbanCardInteractionControls'
import { KanbanCardLabelBars } from './KanbanCardLabelBars'
import { KanbanCardMobileMoreButton } from './KanbanCardMobileMoreButton'
import { KanbanCardTags } from './KanbanCardTags'
import { KanbanCardTitleRow } from './KanbanCardTitleRow'
import { KanbanQuoteCardFooter } from './KanbanQuoteCardFooter'
import type { KanbanCardThreadSupport } from './kanbanCardThreadSupport'
import type { useKanbanCardPresentation } from './useKanbanCardPresentation'

type RailProps = Omit<KanbanCardInteractionControlsProps, 'mode'>

/** Field ids gated while assembling title / action / email / footer slots. */
export const CARD_FACE_SLOT_FIELD_IDS = [
  'pin',
  'aiConfidence',
  'sensitive',
  'actionDate',
  'actionNote',
  'previousAction',
  'blocked',
  'sender',
  'received',
  'assignees',
  'checklist',
  'comments',
  'mail',
  'workbook',
  'related',
  'unseen',
] as const

export function buildKanbanCardFaceSlots(args: {
  card: KanbanCardProps['card']
  resolvedFace: ResolvedCardFacePrefs
  isCompact: boolean
  isLarge: boolean
  isPhone: boolean
  isQuoteCard: boolean
  hoverActive: boolean
  hideActionBar: boolean
  hideCapture: boolean
  showDue: boolean
  overdue: boolean
  hovered: boolean
  emailMeta: ReturnType<typeof useKanbanCardPresentation>['emailMeta']
  assignedMembers: ReturnType<typeof useKanbanCardPresentation>['assignedMembers']
  checklistDone: number
  checklistTotal: number
  commentCount: number
  relatedCount: number
  unseenCount: number
  checklistApiReady: boolean
  checklistExpanded: boolean
  setChecklistExpanded: Dispatch<SetStateAction<boolean>>
  onOpen?: KanbanCardProps['onOpen']
  onRenameInline?: KanbanCardProps['onRenameInline']
  onToggleComplete?: KanbanCardProps['onToggleComplete']
  highlightQuery?: string
  aiConfidencePct: number | null
  lowConfidence: boolean
  quickActions?: KanbanCardQuickActionHandlers
  onOpenEmail?: KanbanCardProps['onOpenEmail']
  onContextMenu?: KanbanCardProps['onContextMenu']
  threadSupport: KanbanCardThreadSupport
  interactionControlProps: RailProps
  togglePhoneChrome: () => void
  laneKind?: KanbanCardProps['laneKind']
}): Record<string, ReactNode> {
  const {
    card, resolvedFace, isCompact, isLarge, isPhone, isQuoteCard, hoverActive,
    hideActionBar, hideCapture, showDue, overdue, hovered, emailMeta,
    assignedMembers, checklistDone, checklistTotal, commentCount, relatedCount,
    unseenCount, checklistApiReady, checklistExpanded, setChecklistExpanded,
    onOpen, onRenameInline, onToggleComplete, highlightQuery, aiConfidencePct,
    lowConfidence, quickActions, onOpenEmail, onContextMenu, threadSupport,
    interactionControlProps, togglePhoneChrome, laneKind,
  } = args
  return {
    labels: isCompact ? <KanbanCardLabelBars card={card} /> : <KanbanCardTags card={card} />,
    title: (
      <>
      <KanbanCardTitleRow
        card={card}
        isCompact={isCompact}
        large={isLarge}
        completeVisible={
          resolvedFace.complete === 'always' ||
          (resolvedFace.complete === 'hover' && hoverActive)
        }
        onOpen={onOpen}
        onRenameInline={onRenameInline}
        onToggleComplete={
          resolvedFace.complete === 'off' ? undefined : onToggleComplete
        }
        highlightQuery={highlightQuery}
        aiConfidencePct={aiConfidencePct}
        lowConfidence={lowConfidence}
        hidePin={!resolvedFace.fields.pin}
        hideAi={!resolvedFace.fields.aiConfidence}
        hideSensitive={!resolvedFace.fields.sensitive}
      />
      {!isCompact && card.summary ? (
        <p className="mt-1 line-clamp-4 whitespace-pre-wrap break-words text-[12px] leading-snug text-neutral-600 dark:text-zinc-300">
          {card.summary}
        </p>
      ) : null}
      </>
    ),
    actionLine: !isCompact ? (
      <KanbanCardActionLine
        card={card}
        onSetActionDate={
          resolvedFace.fields.actionDate ? quickActions?.onSetActionDate : undefined
        }
        showNote={resolvedFace.fields.actionNote}
        showDate={resolvedFace.fields.actionDate}
        showPrevious={resolvedFace.fields.previousAction}
      />
    ) : null,
    stateStrip:
      !isCompact && resolvedFace.fields.blocked ? (
        <KanbanCardStateStrip card={card} />
      ) : null,
    emailMeta:
      !isCompact &&
      emailMeta &&
      (resolvedFace.fields.sender || resolvedFace.fields.received) ? (
        <KanbanCardEmailMeta
          meta={emailMeta}
          showSender={resolvedFace.fields.sender}
          showReceived={resolvedFace.fields.received}
        />
      ) : null,
    meta: (
      <KanbanCardFooter
        card={card}
        isCompact={isCompact}
        emailMeta={emailMeta}
        assignedMembers={resolvedFace.fields.assignees ? assignedMembers : []}
        checklistDone={resolvedFace.fields.checklist ? checklistDone : 0}
        checklistTotal={resolvedFace.fields.checklist ? checklistTotal : 0}
        commentCount={resolvedFace.fields.comments ? commentCount : 0}
        overdue={overdue}
        hovered={hovered}
        hideActionPill={!isCompact && !!card.action_date}
        showDue={showDue}
        onSetActionDate={quickActions?.onSetActionDate}
        onSetDueDate={showDue ? quickActions?.onSetDueDate : undefined}
        checklistApiReady={checklistApiReady}
        checklistExpanded={checklistExpanded}
        onToggleChecklist={() => setChecklistExpanded((value) => !value)}
        onOpenEmail={resolvedFace.fields.mail ? onOpenEmail : undefined}
        onOpenWorkbook={
          resolvedFace.fields.workbook ? quickActions?.onOpenWorkbook : undefined
        }
        relatedCount={resolvedFace.fields.related ? relatedCount : 0}
        unseenCount={resolvedFace.fields.unseen ? unseenCount : 0}
        fields={resolvedFace.fields}
        trailingSlot={
          isPhone && (quickActions || onContextMenu || threadSupport.canShowThread) ? (
            <>
              <KanbanCardInteractionControls mode="phone" {...interactionControlProps} />
              {(quickActions || onContextMenu) && (
                <KanbanCardMobileMoreButton
                  card={card}
                  chromeMode={!!quickActions}
                  onPress={(anchor) => {
                    if (quickActions) {
                      togglePhoneChrome()
                      return
                    }
                    onContextMenu?.(card, anchor)
                  }}
                />
              )}
            </>
          ) : undefined
        }
      />
    ),
    quoteFacts: isQuoteCard ? (
      <KanbanQuoteCardFooter card={card} laneKind={laneKind} compact={isCompact} />
    ) : null,
    actionBar: !isPhone && hoverActive && !hideActionBar ? (
      <KanbanCardInteractionControls mode="desktop" {...interactionControlProps} hideCapture />
    ) : null,
    capture: !isPhone && hoverActive && !hideCapture ? (
      <KanbanCardInteractionControls mode="desktop" {...interactionControlProps} hideActionBar />
    ) : null,
  }
}
